import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { AuthProfile, PasswordLoginResult, Session, VerifyOtpResult } from '../types'
import { AuthService } from '../services/AuthService'
import { setUnauthorizedHandler } from '../services/http'
import { isJwtExpired } from '../utils/jwt'

/**
 * Auth-Anbindung an das echte Backend: Passwort-/OTP-Login → JWT, Session aus
 * dem `account`-Profil (bzw. `GET /auth/me`). Feldnamen folgen dem Backend
 * (`token`/`account`/`deviceToken`).
 */
type Status = 'loading' | 'anon' | 'authed'

interface AuthContextValue {
  session: Session | null
  status: Status
  requestOtp: (email: string) => Promise<void>
  passwordLogin: (email: string, password: string) => Promise<PasswordLoginResult>
  verifyOtp: (input: { email: string; code: string; trustDevice?: boolean }) => Promise<VerifyOtpResult>
  logout: () => void
  refreshMe: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Gültigkeitsdauer eines Trusted-Device-Tokens (Spec §8): 30 Tage. */
const TRUSTED_DEVICE_TTL_MS = 30 * 24 * 60 * 60 * 1000

/** Backend-Profil → Session. `active` liefert /auth/me nicht — eingeloggt ⇒ aktiv. */
function profileToSession(p: AuthProfile): Session {
  return {
    id: p.id,
    email: p.email,
    displayName: p.displayName,
    role: p.role,
    brandKey: p.brandKey,
    vcardUrlKey: p.vcardUrlKey,
    active: true,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [status, setStatus] = useState<Status>('loading')

  // Markiert eine laufende Nutzeraktion, damit ein danach auflösender Mount-
  // Bootstrap (me()) die frisch gesetzte Session nicht überschreibt.
  const interactedRef = useRef(false)

  const logout = useCallback(() => {
    interactedRef.current = true
    // Token verwerfen, Trusted-Device bleibt (spart erneutes OTP nach Re-Login).
    AuthService.clearToken()
    setSession(null)
    setStatus('anon')
  }, [])

  // Bei 401 (http-Interceptor) automatisch ausloggen.
  useEffect(() => {
    setUnauthorizedHandler(() => logout())
  }, [logout])

  // Mount-Bootstrap: gültiges, nicht abgelaufenes Token → Profil laden.
  useEffect(() => {
    let cancelled = false

    async function bootstrap() {
      const token = AuthService.getToken()
      if (!token || isJwtExpired(token)) {
        if (!interactedRef.current) AuthService.clearToken()
        if (!cancelled && !interactedRef.current) setStatus('anon')
        return
      }
      try {
        const profile = await AuthService.me()
        if (!cancelled && !interactedRef.current) {
          setSession(profileToSession(profile))
          setStatus('authed')
        }
      } catch {
        if (!interactedRef.current) AuthService.clearToken()
        if (!cancelled && !interactedRef.current) {
          setSession(null)
          setStatus('anon')
        }
      }
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [])

  const requestOtp = useCallback(async (email: string) => {
    await AuthService.requestOtp(email)
  }, [])

  const passwordLogin = useCallback(async (email: string, password: string): Promise<PasswordLoginResult> => {
    interactedRef.current = true
    const deviceToken = AuthService.getValidTrustedDevice(email)?.token
    const result = await AuthService.passwordLogin(email, password, deviceToken)
    // Trusted-Device-Treffer (Admin): Backend liefert direkt token + account.
    if (result.token && result.account) {
      AuthService.setToken(result.token)
      setSession(profileToSession(result.account))
      setStatus('authed')
    }
    return result
  }, [])

  const verifyOtp = useCallback(
    async (input: { email: string; code: string; trustDevice?: boolean }): Promise<VerifyOtpResult> => {
      interactedRef.current = true
      const result = await AuthService.verifyOtp(input)
      AuthService.setToken(result.token)
      if (result.deviceToken) {
        AuthService.setTrustedDevice({
          token: result.deviceToken,
          email: input.email,
          expiresAt: Date.now() + TRUSTED_DEVICE_TTL_MS,
        })
      }
      setSession(profileToSession(result.account))
      setStatus('authed')
      return result
    },
    [],
  )

  const refreshMe = useCallback(async () => {
    const profile = await AuthService.me()
    setSession(profileToSession(profile))
    setStatus('authed')
  }, [])

  return (
    <AuthContext.Provider value={{ session, status, requestOtp, passwordLogin, verifyOtp, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
